import React, { useMemo, useState } from "react";
import { Check, X, PenLine, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { EXT_STATUS_FR, EXT_TONE, extractedValue, fmtTermValue } from "./negotiationShared";

const FILTERS = [
  ["all", tfr("Tous")], ["pending", tfr("À vérifier")], ["differences", tfr("Écarts")],
  ["validated", tfr("Validés")], ["ignored", tfr("Ignorés")], ["not_found", tfr("Non trouvés")],
];

export function ExtractionReview({ open, onOpenChange, neg, documentId, documents, extractions, comparison, draft, onChanged }) {
  const { tf, locale } = useLang();
  const [filter, setFilter] = useState("pending");
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const doc = documents.find((d) => d.id === documentId);
  const rows = useMemo(() => extractions.filter((x) => x.document_id === documentId), [extractions, documentId]);
  const compByKey = useMemo(() => Object.fromEntries(comparison.map((r) => [r.term_key, r])), [comparison]);
  const draftByKey = useMemo(() => Object.fromEntries((draft?.terms || []).map((t) => [t.term_key, t])), [draft]);

  const currentOf = (x) => {
    const dt = draftByKey[x.term_key];
    if (dt) return ["money", "number", "percent"].includes(dt.vtype) ? dt.amount : dt.vtype === "bool" ? dt.value_bool : dt.value_text;
    const c = compByKey[x.term_key];
    if (!c) return null;
    return c.latest ?? c.ours ?? c.initial;
  };
  const isDiff = (x) => {
    const cur = currentOf(x); const ext = extractedValue(x);
    if (ext === null || ext === undefined || cur === null || cur === undefined) return false;
    if (["money", "number", "percent"].includes(x.vtype)) return Math.abs(Number(ext) - Number(cur)) >= 0.005;
    return String(ext).trim().toLowerCase() !== String(cur).trim().toLowerCase();
  };

  const filtered = rows.filter((x) => {
    if (filter === "pending") return ["pending_review", "needs_review"].includes(x.status);
    if (filter === "differences") return ["pending_review", "needs_review"].includes(x.status) && isDiff(x);
    if (filter === "validated") return ["accepted", "modified_and_accepted"].includes(x.status);
    if (filter === "ignored") return x.status === "ignored";
    if (filter === "not_found") return x.status === "not_found";
    return true;
  });
  const counts = {
    detected: rows.filter((x) => x.status !== "not_found").length,
    pending: rows.filter((x) => ["pending_review", "needs_review"].includes(x.status)).length,
    validated: rows.filter((x) => ["accepted", "modified_and_accepted"].includes(x.status)).length,
    ignored: rows.filter((x) => x.status === "ignored").length,
    not_found: rows.filter((x) => x.status === "not_found").length,
  };

  const decide = async (x, action, value) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/negotiations/${neg.id}/extractions/${x.id}/decide`, { action, value });
      toast.success(action === "ignore" ? tf("Suggestion ignorée") : tf("Valeur validée dans le brouillon"));
      setEditing(null);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };

  const acceptAllUnchanged = async () => {
    const safe = rows.filter((x) => x.status === "pending_review" && !isDiff(x) && currentOf(x) !== null && currentOf(x) !== undefined);
    if (!safe.length) { toast.info(tf("Aucun terme identique à valider en lot.")); return; }
    setBusy(true);
    try {
      for (const x of safe) await api.post(`/negotiations/${neg.id}/extractions/${x.id}/decide`, { action: "accept" });
      toast.success(`${safe.length} ${tf("termes identiques validés")}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };

  const safeIdx = Math.min(idx, Math.max(0, filtered.length - 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="neg-extraction-review">
        <DialogTitle>{tf("Conditions extraites")}</DialogTitle>
        <DialogDescription>
          {doc?.filename} · {counts.detected} {tf("termes détectés")} · {counts.pending} {tf("à vérifier")} · {counts.not_found} {tf("non trouvés")}
          <span className="block mt-0.5">{tf("Chaque valeur extraite doit être validée par vous avant d'entrer dans la négociation.")}</span>
        </DialogDescription>

        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setFilter(k); setIdx(0); }} data-testid={`ext-filter-${k}`}
              className={cn("text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                filter === k ? "border-red-500/50 text-red-500 bg-red-500/10" : "border-border dark:border-border text-muted-foreground hover:text-foreground")}>
              {tf(l)}
            </button>
          ))}
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="h-8" disabled={busy} onClick={acceptAllUnchanged} data-testid="ext-accept-all-unchanged">
            {tf("Valider les termes identiques")}
          </Button>
        </div>

        {!draft && counts.pending > 0 && (
          <p className="text-[11px] text-amber-500 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2" data-testid="ext-no-draft-hint">
            {tf("Créez d'abord un brouillon de version pour recevoir les valeurs validées.")}
          </p>
        )}

        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="ext-empty">{tf("Aucun terme dans ce filtre.")}</p>
        ) : (
          <>
            {/* Desktop list */}
            <div className="hidden md:block space-y-2">
              {filtered.map((x) => (
                <ExtRow key={x.id} x={x} cur={currentOf(x)} diff={isDiff(x)} busy={busy} decide={decide}
                  editing={editing} setEditing={setEditing} tf={tf} locale={locale} />
              ))}
            </div>
            {/* Mobile: one card + prev/next */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-2">
                <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={safeIdx === 0} data-testid="ext-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-xs text-muted-foreground">{safeIdx + 1}/{filtered.length}</span>
                <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setIdx((i) => Math.min(filtered.length - 1, i + 1))} disabled={safeIdx >= filtered.length - 1} data-testid="ext-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
              <ExtRow x={filtered[safeIdx]} cur={currentOf(filtered[safeIdx])} diff={isDiff(filtered[safeIdx])} busy={busy}
                decide={decide} editing={editing} setEditing={setEditing} tf={tf} locale={locale} mobile />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ExtRow({ x, cur, diff, busy, decide, editing, setEditing, tf, locale, mobile }) {
  const extVal = extractedValue(x);
  const numeric = ["money", "number", "percent"].includes(x.vtype);
  const delta = numeric && diff && extVal !== null && cur !== null ? Math.round((Number(extVal) - Number(cur)) * 100) / 100 : null;
  const pending = ["pending_review", "needs_review"].includes(x.status);
  const isEditing = editing === x.id;
  const fmt = (v) => fmtTermValue(x.vtype, (x.value || {}).currency || (x.accepted_value || {}).currency, v, locale, tf);

  return (
    <div className={cn("rounded-xl border border-border/60 dark:border-border p-3", diff && pending && "border-amber-500/40")} data-testid={`ext-row-${x.term_key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-sm">{tf(x.label)}</span>
        <span className={cn("text-[10px] px-2 py-0.5 rounded-full border", EXT_TONE[x.status])} data-testid={`ext-status-${x.term_key}`}>{tf(EXT_STATUS_FR[x.status])}</span>
        <div className="flex-1" />
        {x.source_page && <span className="text-[10px] text-muted-foreground">{tf("Source")} : {x.document_name} · {tf("Page")} {x.source_page}</span>}
        {!x.source_page && <span className="text-[10px] text-muted-foreground">{tf("Source")} : {x.document_name}</span>}
      </div>
      <div className={cn("mt-2 gap-2 text-sm", mobile ? "space-y-1.5" : "grid grid-cols-3")}>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tf("Valeur extraite")}</div>
          <div className="font-medium" data-testid={`ext-value-${x.term_key}`}>{x.status === "not_found" ? tf("Non trouvé") : fmt(extVal)}</div>
          {x.source_quote && <div className="text-[10px] text-muted-foreground italic truncate" title={x.source_quote}>« {x.source_quote} »</div>}
        </div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tf("Valeur actuelle")}</div>
          <div className="font-medium" data-testid={`ext-current-${x.term_key}`}>{fmt(cur)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tf("Différence")}</div>
          {delta !== null ? (
            <div className={cn("font-medium", delta > 0 ? "text-emerald-500" : "text-red-500")} data-testid={`ext-delta-${x.term_key}`}>{delta > 0 ? "+" : ""}{fmt(delta)}</div>
          ) : diff && pending ? (
            <div className="text-amber-500 text-xs font-medium">{fmt(cur)} → {fmt(extVal)}</div>
          ) : <div className="text-muted-foreground">—</div>}
        </div>
      </div>
      {["accepted", "modified_and_accepted"].includes(x.status) && (
        <p className="text-[10px] text-emerald-500 mt-1.5">{tf("Validé par")} {x.decided_by} · {x.decided_at ? new Date(x.decided_at).toLocaleDateString(locale) : ""}</p>
      )}
      {isEditing ? (
        <ModifyEditor x={x} busy={busy} tf={tf} onCancel={() => setEditing(null)} onConfirm={(value) => decide(x, "modify", value)} />
      ) : pending && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <Button variant="ghost" size="sm" className="h-8 text-muted-foreground" disabled={busy} onClick={() => decide(x, "ignore")} data-testid={`ext-ignore-${x.term_key}`}>
            <X className="h-3.5 w-3.5 mr-1" /> {tf("Ignorer")}
          </Button>
          <Button variant="outline" size="sm" className="h-8" disabled={busy} onClick={() => setEditing(x.id)} data-testid={`ext-modify-${x.term_key}`}>
            <PenLine className="h-3.5 w-3.5 mr-1" /> {tf("Modifier")}
          </Button>
          {x.status === "pending_review" && (
            <Button size="sm" className="h-8" disabled={busy} onClick={() => decide(x, "accept")} data-testid={`ext-accept-${x.term_key}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />} {tf("Accepter")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ModifyEditor({ x, busy, tf, onCancel, onConfirm }) {
  const v = x.value || {};
  const numeric = ["money", "number", "percent"].includes(x.vtype);
  const [amount, setAmount] = useState(v.amount ?? "");
  const [currency, setCurrency] = useState(v.currency || "EUR");
  const [text, setText] = useState(v.value_text || "");
  const [boolVal, setBoolVal] = useState(!!v.value_bool);
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2.5" data-testid={`ext-edit-${x.term_key}`}>
      {numeric ? (<>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 w-36" data-testid={`ext-edit-amount-${x.term_key}`} />
        {x.vtype === "money" && (
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="h-9 w-[76px]"><SelectValue /></SelectTrigger>
            <SelectContent>{["EUR", "USD", "GBP", "CHF"].map((c) => <SelectItem key={c} value={c}>{tfr(c)}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </>) : x.vtype === "bool" ? (
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={boolVal} onCheckedChange={(c) => setBoolVal(!!c)} /> {boolVal ? tf("Oui") : tf("Non")}</label>
      ) : (
        <Input value={text} onChange={(e) => setText(e.target.value)} className="h-9 flex-1 min-w-[160px]" data-testid={`ext-edit-text-${x.term_key}`} />
      )}
      <Button variant="ghost" size="sm" className="h-9" onClick={onCancel}>{tf("Annuler")}</Button>
      <Button size="sm" className="h-9" disabled={busy || (numeric && amount === "")} data-testid={`ext-edit-confirm-${x.term_key}`}
        onClick={() => onConfirm(numeric ? { amount: Number(amount), currency } : x.vtype === "bool" ? { value_bool: boolVal } : { value_text: text })}>
        {tf("Valider la valeur corrigée")}
      </Button>
    </div>
  );
}
